import { FeatureFlagsRulesServiceBase, HttpService, LocalContext, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentPaymentListItemModel, ExchangeRateModel, RelatedEntity, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentItemModel, serializeDocumentPaymentListItemModel } from '../common/serialziation-utils';
import { ProductJpkVatGroupCollectionQuery } from '../product/queries/product-jpk-vat-group.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { SaleJpkVatGroupCollectionQuery } from './queries/sale-jpk-vat-group.query-base';
import { SaleAdvanceBO } from './sale-advance.bo';

export interface SaleDocumentItemCurrency {
    precision: number;
    symbol: string;
}

@Repository({
    boType: SaleAdvanceBO,
    apiController: TaggedUrl.join('sales', [Tags.Api]),
    dto: {
        get: SaleGetDocumentDTO,
        update: SaleUpdateDocumentDTO
    }
})
export class SaleAdvanceRepositoryBase extends RepositoryBase<SaleAdvanceBO, SaleAdvanceDocumentModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO> {
    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private readonly clientRepository: ClientRepositoryBase,
        private readonly productRepository: ProductRepositoryContract,
        private readonly queryBus: QueryBus,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(http);
    }

    protected createBO(): SaleAdvanceBO {
        return new SaleAdvanceBO(this.weaver, this.clientRepository, this.featureFlagsRulesService, this.queryBus, this.productRepository);
    }

    public async fromPlainObjectAsync(item: SaleGetDocumentDTO): Promise<SaleAdvanceDocumentModel> {
        const document = new SaleAdvanceDocumentModel(pickBy<Partial<SaleAdvanceDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,
            inventoryEffect: item.inventoryEffect,

            ddkState: item.ddkState,
            eppState: item.eppState,
            fiscalizationState: item.fiscalizationState,
            jpk_VATState: item.jpk_vatState,
            orderState: item.orderState,

            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,

            advanceValue: item.advanceValue,
            advanceState: item.advanceState,

            issueDate: item.issueDate,
            goodsIssueDate: item.goodsIssueDate || ((item.inventoryEffect && item.deliveryDate) || null),
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            kind: item.kind,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            invoiceToReceipt: item.invoiceToReceipt,
            marginSchemeId: item.marginSchemeId,
            netValue: item.totalNetValue,
            note: item.note,
            numberingId: item.numberingId,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            saleDate: item.deliveryDate,
            splitPayment: item.splitPayment,
            taxLiabilityDate: item.taxLiabilityDate,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            customDueDateDescription: item.customDueDateDescription,
            ksefProcessingStage: item.ksefProcessingStage,
            ksefDocumentStatus: item.ksefDocumentStatus,
            ksefDocumentId: item.ksefDocumentId,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefAcquisitionDateTime: item.ksefAcquisitionDateTime,
            ksefLastStatusCheckDate: item.ksefLastStatusCheckDate,
            ksefIssueDate: item.ksefIssueDate,
            ksefSessionReferenceNumber: item.ksefSessionReferenceNumber,
            ksefLastReceivingUpoDate: item.ksefLastReceivingUpoDate,
            ksefSessionState: item.ksefSessionState,
            ksefSessionEndDate: item.ksefSessionEndDate,
            ksefAllDocumentsProcessed: item.ksefAllDocumentsProcessed,
            ksefInitializationDate: item.ksefInitializationDate,
            ksefHash: item.ksefHash,
            ksefUpoDownloadUrl: item.ksefUpoDownloadUrl,
            ksefUpoDownloadUrlExpirationDate: item.ksefUpoDownloadUrlExpirationDate,
            ksefSubmissionMode: item.ksefSubmissionMode,
            ksefCertificateUrl: item.ksefCertificateUrl,
            ksefXmlExported: item.ksefXmlExported,
            ksefXmlImported: item.ksefXmlImported,
            ksefXmlUrl: item.ksefXmlUrl,
            orderNumber: item.additionalData?.orderNumber,
            orderDate: item.additionalData?.orderDate,
            agreementNumber: item.additionalData?.agreementNumber,
            agreementDate: item.additionalData?.agreementDate,
            rateNP: item.rateNP,
            reverseCharge: item.reverseCharge
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            name: item.deliveryAddress.name,
            zipCode: item.deliveryAddress.zipCode,
            line1: item.deliveryAddress.address,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleAdvanceDocumentItemModel(pickBy<Partial<SaleAdvanceDocumentItemModel>>({
                advance: documentItem.advance,
                cost: documentItem.cost,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                includesInventoryControl: documentItem.includesInventoryControl,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                id: documentItem.id,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                cnCode: documentItem.cnCode,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0));
        }

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,
                date: item.date,
                value: item.amount,
                onDocument: item.onDocument
            }, val => val !== void 0));
        }));

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat
            }, val => val !== void 0));
        })) : new ObservableCollection();

        return document;
    }

    protected async preparePlainObjectAsync(model: SaleAdvanceDocumentModel, ctx: LocalContext): Promise<SaleUpdateDocumentDTO> {
        const jpkVatDocumentGroupCodes = await ctx.getAsync(SaleJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));
        const jpkVatProductGroupCodes = await ctx.getAsync(ProductJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));

        const item: SaleUpdateDocumentDTO = {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            advanceId: model.advanceId ? model.advanceId : null,
            firstAdvanceDocumentVersion: model.firstAdvanceDocumentVersion ? model.firstAdvanceDocumentVersion : null,
            firstAdvanceDocumentId: model.firstAdvanceDocumentId ? model.firstAdvanceDocumentId : null,
            advanceValue: model.advanceValue,
            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            inventoryEffect: model.inventoryEffect,

            bankAccountId: model.bankAccountNumberId as any,
            baseCurrencyBankAccountId: model.baseCurrencyBankAccountNumberId as any,
            buyer: model.buyer ? serializeDocumentBuyerModel(model.buyer) : null as any,
            calculationMethod: model.calculationMethod,
            cashBasis: model.cashBasis,
            client: model.client ? serializeDocumentClientModel(model.client) : null as any,
            communityTransactionId: model.communityTransactionId,
            currencyId: model.currencyId,
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null as any,
            deliveryDate: model.saleDate ? getStartOfDate(model.saleDate) : null as any,
            documentNumber: model.documentNumber,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),
            dueDate: getStartOfDate(model.dueDate),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null as any,
            favourite: model.favourite,
            issuedByVatPayer: model.issuedByVatPayer,
            issueDate: getStartOfDate(model.issueDate),
            goodsIssueDate: model.goodsIssueDate ? getStartOfDate(model.goodsIssueDate) : null,
            issuer: model.issuer,
            items: model.items.map(serializeDocumentItemModel),
            jpkVatDocumentGroups: model.jpkVatGroups.filter(g => jpkVatDocumentGroupCodes.includes(g)),
            jpkVatProductGroups: model.jpkVatGroups.filter(g => jpkVatProductGroupCodes.includes(g)),
            invoiceToReceipt: model.invoiceToReceipt,
            kind: model.kind,
            numberingId: model.numberingId,
            marginSchemeId: model.marginSchemeId,
            note: model.note,
            paymentMethodId: model.paymentMethodId,
            recipient: model.recipient,
            splitPayment: model.splitPayment,
            taxLiabilityDate: getStartOfDate(model.taxLiabilityDate),
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            customDueDateDescription: model.customDueDateDescription,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName as any,
            vatReleaseReasonSource: model.vatReleaseReasonSource as any,
            ksefSubmissionMode: model.ksefSubmissionMode,
            additionalData: {
                orderNumber: model.orderNumber,
                orderDate: model.orderDate ? getStartOfDate(model.orderDate) : null as any,
                agreementNumber: model.agreementNumber,
                agreementDate: model.agreementDate ? getStartOfDate(model.agreementDate) : null as any
            },
            rateNP: model.rateNP,
            reverseCharge: model.reverseCharge,
            adjustPaymentDate: false
        };
        if (model.id && model.documentNumber) {
            item.documentNumber = model.documentNumber;
        }
        if (model.implementedDocumentId) {
            item.implementedDocumentId = model.implementedDocumentId;
            item.implementedDocumentVersion = model.implementedDocumentVersion;
        }
        return item;
    }
}
